import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { fromEvent, Subject, throttleTime, filter, map } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type ActivityType =
  | 'click'
  | 'touchstart'
  | 'touchmove'
  | 'mousemove'
  | 'keydown'
  | 'scroll';

@Injectable({
  providedIn: 'root',
})
export class ActivityDetectorService {
  private readonly destroy$ = new Subject<void>();

  private readonly activityDetectedSubject = new Subject<ActivityType>();
  readonly activityDetected$ = this.activityDetectedSubject.asObservable();

  // Signal para tracking interno (opcional)
  lastActivity = signal<ActivityType | null>(null);

  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  constructor() {
    this.destroyRef.onDestroy(() => this.destroy());
    this.ngZone.runOutsideAngular(() => {
      this.setupEventListeners();
    });
  }

  /**
   * Limpia listeners del servicio.
   * Nota: en la práctica, al ser `providedIn: 'root'`, normalmente no se llama.
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.activityDetectedSubject.complete();
  }

  private setupEventListeners(): void {
    // Click events
    fromEvent<MouseEvent>(document, 'click', { capture: true })
      .pipe(
        map(() => 'click' as ActivityType),
        takeUntil(this.destroy$)
      )
      .subscribe(activity => this.emitActivity(activity));

    // Touch activity (simple) //! detecta "hay alguien tocando" sin depender de scroll/move
    // Nota: usamos captura para no depender del burbujeo (ej. stopPropagation en modales).
    const touchTarget: EventTarget = globalThis;
    const supportsPointerEvents = 'PointerEvent' in globalThis;

    if (supportsPointerEvents) {
      fromEvent<PointerEvent>(touchTarget, 'pointerdown', { capture: true })
        .pipe(
          filter(event => event.pointerType === 'touch'),
          map(() => 'touchstart' as ActivityType),
          takeUntil(this.destroy$)
        )
        .subscribe(activity => this.emitActivity(activity));
    } else {
      fromEvent<TouchEvent>(touchTarget, 'touchstart', { capture: true })
        .pipe(
          map(() => 'touchstart' as ActivityType),
          takeUntil(this.destroy$)
        )
        .subscribe(activity => this.emitActivity(activity));
    }

    // Keyboard events
    fromEvent<KeyboardEvent>(document, 'keydown', { capture: true })
      .pipe(
        throttleTime(1000),
        map(() => 'keydown' as ActivityType),
        takeUntil(this.destroy$)
      )
      .subscribe(activity => this.emitActivity(activity));

    // Scroll events
    fromEvent(document, 'scroll', { passive: true, capture: true })
      .pipe(
        throttleTime(1000),
        map(() => 'scroll' as ActivityType),
        takeUntil(this.destroy$)
      )
      .subscribe(activity => this.emitActivity(activity));
  }

  private emitActivity(activity: ActivityType): void {
    this.ngZone.run(() => {
      this.lastActivity.set(activity);
      this.activityDetectedSubject.next(activity);
    });
  }
}
